import { useEffect, useState } from 'react';

const MOBILE_DEVICE_UA_REGEX =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
type TViewportOrientation = 'portrait' | 'landscape';

export const isMobileDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const userAgentMobile = MOBILE_DEVICE_UA_REGEX.test(navigator.userAgent);
  const uaDataMobile =
    'userAgentData' in navigator &&
    // userAgentData is still experimental and not present in all browsers.
    Boolean(
      (navigator as Navigator & { userAgentData?: { mobile?: boolean } })
        .userAgentData?.mobile
    );

  const isTouchDevice =
    navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const hasNoHover = window.matchMedia('(hover: none)').matches;

  return (
    uaDataMobile ||
    userAgentMobile ||
    (isTouchDevice && hasCoarsePointer && hasNoHover)
  );
};

const getViewportOrientation = (): TViewportOrientation => {
  if (typeof window === 'undefined') {
    return 'portrait';
  }

  return window.matchMedia('(orientation: landscape)').matches
    ? 'landscape'
    : 'portrait';
};

export function useViewportOrientation(): TViewportOrientation {
  const [orientation, setOrientation] =
    useState<TViewportOrientation>('portrait');

  useEffect(() => {
    const orientationQuery = window.matchMedia('(orientation: landscape)');

    const updateOrientation = () => {
      setOrientation(getViewportOrientation());
    };

    updateOrientation();

    if (orientationQuery.addEventListener) {
      orientationQuery.addEventListener('change', updateOrientation);
      window.addEventListener('orientationchange', updateOrientation);
      window.addEventListener('resize', updateOrientation);

      return () => {
        orientationQuery.removeEventListener('change', updateOrientation);
        window.removeEventListener('orientationchange', updateOrientation);
        window.removeEventListener('resize', updateOrientation);
      };
    }

    orientationQuery.addListener(updateOrientation);
    window.addEventListener('orientationchange', updateOrientation);
    window.addEventListener('resize', updateOrientation);

    return () => {
      orientationQuery.removeListener(updateOrientation);
      window.removeEventListener('orientationchange', updateOrientation);
      window.removeEventListener('resize', updateOrientation);
    };
  }, []);

  return orientation;
}

function useIsMobileViewport() {
  const [isMobileViewport, setIsMobileViewport] = useState(isMobileDevice);

  useEffect(() => {
    const pointerQuery = window.matchMedia('(pointer: coarse)');
    const hoverQuery = window.matchMedia('(hover: none)');

    const updateMobileState = () => {
      setIsMobileViewport(isMobileDevice());
    };

    const handleMediaChange = () => {
      updateMobileState();
    };

    updateMobileState();

    if (pointerQuery.addEventListener && hoverQuery.addEventListener) {
      pointerQuery.addEventListener('change', handleMediaChange);
      hoverQuery.addEventListener('change', handleMediaChange);
      window.addEventListener('orientationchange', updateMobileState);

      return () => {
        pointerQuery.removeEventListener('change', handleMediaChange);
        hoverQuery.removeEventListener('change', handleMediaChange);
        window.removeEventListener('orientationchange', updateMobileState);
      };
    }

    pointerQuery.addListener(handleMediaChange);
    hoverQuery.addListener(handleMediaChange);
    window.addEventListener('orientationchange', updateMobileState);

    return () => {
      pointerQuery.removeListener(handleMediaChange);
      hoverQuery.removeListener(handleMediaChange);
      window.removeEventListener('orientationchange', updateMobileState);
    };
  }, []);

  return isMobileViewport;
}

export default useIsMobileViewport;
